import React, { useState, useEffect, useRef, useMemo } from 'react';
import {
    Box, Card, TextField, Autocomplete, Tooltip, Select, MenuItem, Popper, Typography,
    Table, TableHead, TableRow, TableCell, TableBody,
} from '@mui/material';
import { Colors } from '../../../../Styles/Colors';
import { renderTitle, renderIconButton } from "./SarasCommonComp";
import RestAPIService from '../../../../Utility/Services/RestAPIService';
import { qDoctorAppointmentId } from '../../../../Utility/Services/DoctorPatientQueryIds';
import { Serviceurls } from '../../../../Utility/API/Serviceurls';
import { withTranslation } from 'react-i18next';
import ShowComponents from '../Common/ShowComponent';
import { CommonEditButton } from '../../../../Components/Common Components/CommonComponents';
import CommonValidation from '../../../../Components/CommonFunctions/CommonValidation';
import { DateTime } from 'luxon';
import { debounce } from '../../../../Components/CommonFunctions/CommonFunctions';
import { DateTimePicker, LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { ImagePaths } from '../../../../Utility/ImagePaths';
import { formatDate } from '../../../../Components/CommonFunctions/CommonFunctions';
import { isSameDay  } from 'date-fns';


const headers = ['Duration', 'Review Date & Time', 'Comments', 'Action'];

const commonSx = {
    width: '45%',
    height: 'auto',
    '& .MuiInputBase-root': {
        height: '100%',
    },
};

const SarasReviewPlan = ({ Title, ...props }) => {
    const [state, setState] = useState({
        reviewPlanList: [],
        duration: "",
        durationMode: 'Days',
        reviewDateTime: new Date().setDate(new Date().getDate() + 1),
        comments: "",
        commentsSuggestLst: [],
        isEditClicked: false,
        updateId: null,
    });

    const debouncedGetSuggest = useRef(null);

    const updateState = (updates) => {
        setState((prev) => ({ ...prev, ...updates }));
    };

    useEffect(() => {
        if (state.duration && state.durationMode) {
            const newDate = calculateNewDate(+state.duration, state.durationMode);
            updateState({ reviewDateTime: newDate });
        }
    }, [state.duration, state.durationMode]);

    useEffect(() => {
        debouncedGetSuggest.current = debounce((value) => {
            getSuggestionList(value);
        }, 500);
    }, []);

    const getApiCall = () => {
        getReviewPlanData();
    }

    const getReviewPlanData = () => {
        const { appointmentId } = props
        try {
            let params = ''
            if(props?.fromIp){
                params = Serviceurls.SAR_IP_DOC_NOTES_REVIEWPLAN + `?clinic_id=${props?.clinicId}&ip_admission_id=${props?.admission_id}&doctor_id=${props?.doctor_id}`
            }else{
                params = Serviceurls.SARAS_DOC_REVIEW_PLAN + `?appointment_id=${qDoctorAppointmentId(props?.appointmentId)}`
            }
            RestAPIService.getAll(params)
                .then((response) => {
                    if (response.data.status === "success") {
                        updateState({ reviewPlanList: Object.keys(response.data.data)?.length > 0 ? [response.data.data] : [] })
                    } else {
                        props.errorMessage(response.data.message)
                    }
                }).catch((error) => {
                    props.errorMessage(error?.response?.data?.message || error?.message)
                })
        } catch (e) {
            props.errorMessage(e.message)
        }
    }

    const getSuggestionList = (search = '') => {
        try {
            RestAPIService.getAllTwo(Serviceurls.SARAS_DOC_REVIEW_PLAN_SUGG_GET + `?search_key=${search}&doctor_id=${props?.doctor_id}`)
                .then((response) => {
                    if (response.data.status === "success") {
                        if (state.comments) {
                            updateState({
                                commentsSuggestLst: response?.data?.data || []
                            })
                        }
                    } else {
                        props.errorMessage(response.data.message)
                    }
                }).catch((error) => {
                    props.errorMessage(error.message)
                })
        }
        catch (e) {
            props.errorMessage(e.message)
        }
    }

    const postReviewPlan = () => {
        const { appointmentId, patientId } = props
        const AptDateTime = new Date(state.reviewDateTime)
        const AptDate = DateTime.fromJSDate(AptDateTime).toFormat('yyyy-MM-dd')
        const Aptime = DateTime.fromJSDate(AptDateTime).toLocaleString(DateTime.TIME_24_WITH_SECONDS);
        try {
            let data = {
                "id": state.updateId || null,
                "appointment_id": appointmentId,
                "patient_id": patientId,
                "duration_type": state.durationMode || "Days",
                "duration": state.duration ? +state.duration : null,
                "review_date": AptDate,
                "appointment_time": Aptime,
                "comments": state.comments ? state.comments : '',
            };

            if (props?.fromIp) {
                data['clinic_id'] = props?.clinicId || null
                data['ip_admission_id'] = props?.admission_id || null
                data['doctor_id'] = props?.doctor_id || null
            }
            let path = props?.fromIp ? Serviceurls.SAR_IP_DOC_NOTES_REVIEWPLAN : Serviceurls.SARAS_DOC_REVIEW_PLAN
            updateState({ postLoading: true })
            RestAPIService.create(data, path)
                .then((response) => {
                    if (response?.data?.status === 'success') {
                        props.successMessage(response?.data?.message)
                        onClearHandler()
                        getReviewPlanData()
                        updateState({ postLoading: false })
                        if (props?.fromIp) {
                            props?.callHistory(true)
                        }
                    }
                }).catch((error) => {
                    updateState({ postLoading: false })
                    props.errorMessage(error?.response?.data?.message || error.message)
                })
        } catch (error) {
            updateState({ postLoading: false })
            props.errorMessage(error.message)
        }
    }

    const onClearHandler = () => {
        updateState({
            updateId: null,
            comments: "",
            durationMode: 'Days',
            duration: '',
            reviewDateTime: new Date().setDate(new Date().getDate() + 1),
        })
    }

    const calculateNewDate = (number, duration) => {
        const NewDate = new Date();
        switch (duration) {
            case "Days":
                NewDate.setDate(NewDate.getDate() + number);
                break;
            case "Weeks":
                NewDate.setDate(NewDate.getDate() + 7 * number);
                break;
            case "Months":
                NewDate.setMonth(NewDate.getMonth() + number);
                break;
            case "Years":
                NewDate.setFullYear(NewDate.getFullYear() + number);
                break;
            default:
                break;
        }

        return NewDate;
    };

    const renderTextField = () => {
        return (<TextField
            sx={commonSx}
            value={state.duration || ""}
            autoComplete="off"
            disabled={props.isLocked}
            size="small"
            onChange={(e) => {
                const value = e.target.value;
                if ((CommonValidation.numberOnly(value) && value >= 1 && value <= 20) || value === '') {
                    updateState({ duration: value })
                }
            }}
        />)
    }

    const renderDurationMode = () => {
        return (
            <Select
                sx={commonSx}
                size="small"
                label='Select Duration'
                value={state.durationMode}
                disabled={props.isLocked}
                onChange={(e) => updateState({ durationMode: e.target.value, duration: '' })}
                inputProps={{ emed_tid: "reviewPlan_selectDuration_testId" }}
            >
                <MenuItem value={"Days"}>Days</MenuItem>
                <MenuItem value={"Weeks"}>Weeks</MenuItem>
                <MenuItem value={"Months"}>Months</MenuItem>
                <MenuItem value={"Years"}>Years</MenuItem>
            </Select>
        )
    }

    const renderDateTimeFilter = () => {
        let currentDate = new Date();
        currentDate.setDate(currentDate.getDate() + 1);

        return (
            <LocalizationProvider dateAdapter={AdapterDateFns}>
                <DateTimePicker
                    open={state.DateOpen}
                    onOpen={() => { updateState({ DateOpen: true }) }}
                    onClose={() => { updateState({ DateOpen: false }) }}
                    inputFormat='dd-MM-yyyy & hh:mm a'
                    value={state.reviewDateTime}
                    minDate={currentDate}
                    onChange={(newDate) => {
                        if (newDate === null) {
                            updateState({
                                reviewDateTime: currentDate,
                                duration: ''
                            })
                        } else {
                            const shouldResetDuration = !isSameDay(newDate, state.reviewDateTime);
                            updateState({
                                reviewDateTime: newDate,
                                duration: shouldResetDuration ? '' : state.duration,
                            });
                        }
                    }}
                    renderInput={(params) => <TextField size='small'
                        onKeyDown={(e) => e.preventDefault()}
                        onClick={() => { updateState({ DateOpen: true }) }}
                        {...params}
                        fullWidth
                        variant='outlined'
                        style={{ width: "100%" }}
                    />}
                />
            </LocalizationProvider>
        )
    }

    const renderComments = () => {
        return (<Autocomplete
            size='small'
            clearIcon
            freeSolo
            disablePortal
            sx={{ width: '100%' }}
            options={state.commentsSuggestLst}
            getOptionLabel={(options) => typeof (options) === 'string' ? options : options.value}
            value={state.comments}
            PopperComponent={(props) => (
                <Popper {...props} placement="top" />
            )}
            renderOption={(props, option) => {
                const displayText = option?.length > 100 ? option.slice(0, 100) + "..." : option;
                return (
                    <Tooltip title={option} arrow>
                        <Box
                            component="li"
                            {...props}
                        >
                            <Typography variant="body2">{displayText}</Typography>
                        </Box>
                    </Tooltip>
                );
            }}
            onChange={(e, newValue) => {
                updateState({
                    comments: newValue
                })
            }}
            renderInput={(params) => <TextField
                {...params}
                multiline
                maxRows={2}
                variant="outlined"
                inputProps={{ ...params.inputProps, emed_tid: 'reviewPlan_comments_testId', maxLength: 300 }}
                onChange={(e) => {
                    const { value } = e.target;
                    updateState({
                        comments: value,
                        commentsSuggestLst: value ? state.commentsSuggestLst : []
                    })
                    if (value) {
                        debouncedGetSuggest.current(value);
                    }
                }}
            />}
        />)
    }

    const disableBtn = useMemo(() => {
        const isDurationOrDateMissing = !state.duration && !state.reviewDateTime;
        const result = isDurationOrDateMissing || (state.reviewPlanList?.length > 0 && !state.updateId) || props.isLocked
        return result;
    }, [state.duration, state.reviewDateTime, state.reviewPlanList, state.updateId, props.isLocked]);

    return (
        <ShowComponents onIntersection={getApiCall.bind(this)}>
            <Card sx={{
                width: "100%", height: "fit-content", padding: "0.3vw",
                marginBottom: "0.5vw", border: `2px solid ${props.isSelected ? Colors.themeDark : Colors.white}`
            }}>
                <Box display={"flex"} flexDirection={'row'} alignItems={"center"} justifyContent={'space-between'} padding={"0.5vw"} borderBottom={`1px solid #E3E2E2`}>
                    {renderTitle(Title, props.isSelected)}
                </Box>
                <Table size='small' stickyHeader aria-label="sticky table" sx={{ maxHeight: '20vw', overflowY: 'auto' }}>
                    <TableHead>
                        <TableRow>
                            {headers.map((text, idx) => (
                                <TableCell
                                    key={idx}
                                    align={headers.length - 1 === idx ? "center" : "left"}
                                    sx={{
                                        fontWeight: 600,
                                        fontSize: '0.9vw',
                                        color: Colors.SecondaryText,
                                        backgroundColor: Colors.Background,
                                        px: '0.2vw'
                                    }}
                                >
                                    {text}
                                </TableCell>
                            ))}
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        <TableRow sx={{ height: '3vw', '& td': { px: '0.2vw', verticalAlign: 'middle' } }}>
                            <TableCell align="left" sx={{ width: "12vw" }}>
                                <Box display={"flex"} alignItems={"center"} gap={"0.5vw"} width={"100%"}>
                                    {renderTextField()}
                                    {renderDurationMode()}
                                </Box>
                            </TableCell>
                            <TableCell align="left" sx={{ width: "14vw" }}>{renderDateTimeFilter()}</TableCell>
                            <TableCell align="left" sx={{ width: "16vw" }}>{renderComments()}</TableCell>
                            <TableCell align="center" sx={{ width: "2vw" }}>
                                {renderIconButton('Add', ImagePaths[disableBtn ? 'DisabledAddIcon' : 'AddIcon'].default, postReviewPlan, disableBtn, "2vw")}
                            </TableCell>
                        </TableRow>
                        {
                            state.reviewPlanList?.length > 0 ? state.reviewPlanList?.map((item, index) => (
                                <TableRow key={index} sx={{ '& td': { pl: '0.3vw', pr: "0.2vw", fontSize: "0.9vw" } }}>
                                    <TableCell align="left" style={{ fontSize: "0.9vw" }}>{item?.duration ? `${item?.duration} ${item?.duration_type || ""}` : '-'}</TableCell>
                                    <TableCell align="left">
                                        <div style={{ fontSize: "0.9vw" }}>
                                            {item?.review_date ? formatDate(item?.review_date) : "-"}
                                            {item?.appointment_time ? " & " + DateTime.fromFormat(item?.appointment_time, "HH:mm:ss").toFormat("hh:mm a") : "-"}
                                        </div>
                                    </TableCell>
                                    <TableCell align="left">
                                        {item?.comments ? item?.comments?.length > 15 ?
                                            <Tooltip placement='top' title={item?.comments}>
                                                <Typography fontSize={'0.9vw'}>{item?.comments?.slice(0, 15) + '...'}</Typography>
                                            </Tooltip> :
                                            <Typography fontSize={'0.9vw'}>{item?.comments}</Typography> : '-'}
                                    </TableCell>
                                    <TableCell align="center">
                                        <CommonEditButton disable={state.updateId || props.isLocked} size={"2vw"}
                                            onClick={() => {
                                                updateState({
                                                    updateId: item?.id,
                                                    durationMode: item?.duration_type,
                                                    duration: item?.duration?.toString(),
                                                    comments: item?.comments,
                                                    reviewDateTime: new Date(`${item?.review_date} ${item?.appointment_time}`),
                                                })
                                            }} />
                                    </TableCell>
                                </TableRow>
                            )) : <TableRow>
                                <TableCell colSpan={headers.length} align="center">
                                    No Records To Be Shown
                                </TableCell>
                            </TableRow>}
                    </TableBody>
                </Table>
            </Card>
        </ShowComponents >
    )
}

export default withTranslation()(SarasReviewPlan)